/**
 * Viewport utility actions — zoom, export, etc.
 */
import { getRenderingEngine } from '@cornerstonejs/core';
import { RENDERING_ENGINE_ID } from '../cornerstone/init';

function getViewport(vpId = 'viewport-0'): any {
  return getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport(vpId);
}

/** Zoom to fit the image in the viewport */
export function zoomToFit(vpId = 'viewport-0') {
  const vp = getViewport(vpId);
  if (!vp) return;
  vp.resetCamera();
  vp.render();
}

/** Zoom to 1:1 (actual pixel size) */
export function zoomToActual(vpId = 'viewport-0') {
  const vp = getViewport(vpId);
  if (!vp) return;
  // Reset first, then set zoom to 1.0
  vp.resetCamera();
  vp.setZoom(1.0);
  vp.render();
}

/**
 * Build a composite canvas of the viewport's rendered image plus the SVG
 * annotation layer (lengths, ROIs, arrows, text). Used by the "Include
 * annotations" export option (#9).
 *
 * Cornerstone3D draws the image to a `<canvas>` and overlays measurements on
 * a sibling `<svg class="svg-layer">` inside the viewport host div. To burn
 * the annotations into the image we rasterize the SVG via an offscreen
 * `Image` (data-URL'd from `XMLSerializer`) and composite it on top of a
 * canvas-copy of the rendered pixels. Falls back to the plain canvas if the
 * SVG layer is missing or fails to load — exports never silently break.
 */
async function buildExportCanvas(
  vp: any,
  withAnnotations: boolean
): Promise<HTMLCanvasElement | null> {
  const imgCanvas: HTMLCanvasElement | null = vp.getCanvas?.();
  if (!imgCanvas) return null;
  if (!withAnnotations) return imgCanvas;

  const composite = document.createElement('canvas');
  composite.width = imgCanvas.width;
  composite.height = imgCanvas.height;
  const ctx = composite.getContext('2d');
  if (!ctx) return imgCanvas;
  ctx.drawImage(imgCanvas, 0, 0);

  const host: HTMLElement | undefined = vp.element;
  const svg = host?.querySelector('svg.svg-layer') || host?.querySelector('svg');
  if (!svg) return composite;

  // Clone + force matching dimensions so rasterization fills the canvas.
  const cloned = svg.cloneNode(true) as SVGElement;
  cloned.setAttribute('width', String(composite.width));
  cloned.setAttribute('height', String(composite.height));
  const xml = new XMLSerializer().serializeToString(cloned);
  const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  try {
    await new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, composite.width, composite.height);
        resolve();
      };
      img.onerror = () => reject(new Error('svg rasterize failed'));
      img.src = url;
    });
  } catch {
    // SVG rasterization failed — return image-only rather than nothing
    return imgCanvas;
  } finally {
    URL.revokeObjectURL(url);
  }

  return composite;
}

/** Export current viewport as PNG */
export async function exportAsPng(vpId = 'viewport-0', withAnnotations = false) {
  const vp = getViewport(vpId);
  if (!vp) return;
  const canvas = await buildExportCanvas(vp, withAnnotations);
  if (!canvas) return;

  canvas.toBlob((blob: Blob | null) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `viewport-${Date.now()}.png`;
    a.click();
    URL.revokeObjectURL(url);
  }, 'image/png');
}

/** Export current viewport as JPEG */
export async function exportAsJpeg(
  vpId = 'viewport-0',
  withAnnotations = false,
  quality = 0.92
) {
  const vp = getViewport(vpId);
  if (!vp) return;
  const canvas = await buildExportCanvas(vp, withAnnotations);
  if (!canvas) return;

  canvas.toBlob((blob: Blob | null) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `viewport-${Date.now()}.jpg`;
    a.click();
    URL.revokeObjectURL(url);
  }, 'image/jpeg', quality);
}

/** Download the original DICOM file for the currently displayed image */
export function downloadDicom(vpId = 'viewport-0') {
  const vp = getViewport(vpId);
  if (!vp) return;

  const imageId = vp.getCurrentImageId?.();
  if (!imageId) return;

  // Extract the URL from wadouri:http://...
  const url = imageId.replace('wadouri:', '');
  if (!url) return;

  const a = document.createElement('a');
  a.href = url;
  a.download = `image-${Date.now()}.dcm`;
  a.click();
}
